import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { X } from 'lucide-react';
import { getPhoto, getFile } from '../../storageService';
import { getProviderProfile } from '../../services/providersApi';
import './AdvertisementPopup.css';

// Datos dummy para desarrollo local - movidos fuera del componente
const dummyConfig = {
    display: true,
    title: "¡Destacado del mes!",
    subtitle: "Conocé a este proveedor recomendado por la comunidad",
    providers: [
        {
            username: "dummy_provider_1",
            customText: "Con más de 10 años de experiencia ofreciendo servicios de calidad. Garantizo profesionalismo y dedicación en cada trabajo. ¡Consultá sin compromiso!",
            // Datos dummy del proveedor para desarrollo
            _dummyData: {
                name: "Juan Pérez - Electricista",
                photo: null,
                location: ["Buenos Aires", "La Plata", "Quilmes", "Avellaneda"]
            }
        }
    ]
};

const AdvertisementPopup = () => {
    const navigate = useNavigate();
    const [isVisible, setIsVisible] = useState(false);
    const [loading, setLoading] = useState(false);
    const [popupData, setPopupData] = useState(null);
    const [providers, setProviders] = useState([]);
    const [maxLocations, setMaxLocations] = useState(6); // Default para pantallas grandes

    // Hook para detectar cambios en el tamaño de pantalla
    useEffect(() => {
        const handleResize = () => {
            if (window.innerWidth <= 768) {
                setMaxLocations(7); // Pantallas pequeñas: 4 ubicaciones
            } else {
                setMaxLocations(10); // Pantallas grandes: 6 ubicaciones
            }
        };

        // Establecer valor inicial
        handleResize();

        // Escuchar cambios de tamaño
        window.addEventListener('resize', handleResize);

        // Limpiar listener
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        const fetchPopupData = async () => {
            console.log('[Advertisement Popup] Iniciando carga...');

            // Verificar si el popup ya se mostró en esta sesión
            const hasSeenPopup = sessionStorage.getItem('hasSeenAdvertisementPopup');
            console.log('[Advertisement Popup] hasSeenPopup:', hasSeenPopup);

            if (hasSeenPopup === 'true') {
                console.log('[Advertisement Popup] Ya se mostró en esta sesión, no mostrar.');
                return;
            }

            try {
                setLoading(true);

                let config;

                // Intentar cargar desde Firebase Storage primero
                try {
                    config = await getFile('resources/advertisementpopup.json');
                    console.log('[Advertisement Popup] Config desde Firebase:', config);
                } catch (storageError) {
                    console.warn('[Advertisement Popup] No se pudo cargar desde Firebase Storage, intentando desde public/resources...');

                    // Fallback: cargar desde public/resources para desarrollo local
                    try {
                        const response = await fetch('/resources/advertisementpopup.json');
                        if (response.ok) {
                            config = await response.json();
                            console.log('[Advertisement Popup] Config desde public/resources:', config);
                        } else {
                            console.warn('[Advertisement Popup] No se pudo cargar desde public/resources, usando datos dummy');
                            config = dummyConfig;
                        }
                    } catch (fetchError) {
                        console.warn('[Advertisement Popup] Error cargando desde public/resources, usando datos dummy:', fetchError);
                        config = dummyConfig;
                    }
                }

                // Si display es false, no mostrar el popup
                if (!config) {
                    console.warn('[Advertisement Popup] No se pudo cargar la configuración');
                    return;
                }

                if (!config.display) {
                    console.log('[Advertisement Popup] display es false, no mostrar');
                    return;
                }

                // Validar que tengamos título, subtítulo y proveedores
                if (!config.title || !config.subtitle || !config.providers || config.providers.length === 0) {
                    console.warn('[Advertisement Popup] Configuración incompleta del popup de publicidad');
                    return;
                }

                setPopupData({
                    title: config.title,
                    subtitle: config.subtitle
                });

                // Obtener información completa de cada proveedor (sin esperar las fotos)
                const providersData = await Promise.all(
                    config.providers.map(async (providerConfig) => {
                        try {
                            console.log(`[Advertisement Popup] Cargando proveedor: ${providerConfig.username}`);

                            // Intentar cargar el proveedor real
                            let providerData = null;
                            try {
                                providerData = await getProviderProfile(providerConfig.username);
                            } catch (apiError) {
                                console.warn(`[Advertisement Popup] Error API para ${providerConfig.username}:`, apiError);
                            }

                            // Si no se encuentra el proveedor y hay datos dummy, usarlos
                            if (!providerData && providerConfig._dummyData) {
                                console.log(`[Advertisement Popup] Usando datos dummy para: ${providerConfig.username}`);
                                return {
                                    username: providerConfig.username,
                                    name: providerConfig._dummyData.name,
                                    photo: null,
                                    photoUrl: null,
                                    locations: providerConfig._dummyData.locations || [],
                                    customText: providerConfig.customText || ''
                                };
                            }

                            if (!providerData) {
                                console.warn(`[Advertisement Popup] No se encontró el proveedor: ${providerConfig.username} y no hay datos dummy`);
                                return null;
                            }

                            // Retornar datos del proveedor SIN la foto (se cargará después)
                            return {
                                username: providerData.username,
                                name: providerData.name,
                                photo: providerData.photo,
                                photoUrl: null, // Se cargará de forma asíncrona
                                locations: providerData.locations || [],
                                customText: providerConfig.customText || ''
                            };
                        } catch (error) {
                            console.error(`[Advertisement Popup] Error fetching provider ${providerConfig.username}:`, error);

                            // Si hay un error general y hay datos dummy, usarlos como fallback
                            if (providerConfig._dummyData) {
                                console.log(`[Advertisement Popup] Usando datos dummy por error para: ${providerConfig.username}`);
                                return {
                                    username: providerConfig.username,
                                    name: providerConfig._dummyData.name,
                                    photo: null,
                                    photoUrl: null,
                                    locations: providerConfig._dummyData.location || [],
                                    customText: providerConfig.customText || ''
                                };
                            }

                            return null;
                        }
                    })
                );

                // Filtrar proveedores válidos
                const validProviders = providersData.filter(provider => provider !== null);
                console.log('[Advertisement Popup] Proveedores válidos:', validProviders.length);

                if (validProviders.length === 0) {
                    console.warn('[Advertisement Popup] No hay proveedores válidos');
                    return;
                }

                // Mostrar el popup INMEDIATAMENTE con los datos del backend
                setProviders(validProviders);
                setIsVisible(true);
                console.log('[Advertisement Popup] ✅ Popup mostrado exitosamente');

                // Cargar las fotos de forma ASÍNCRONA (en segundo plano)
                validProviders.forEach(async (provider, index) => {
                    if (provider.photo) {
                        try {
                            const photoUrl = await getPhoto(provider.photo);
                            // Actualizar solo la foto de este proveedor
                            setProviders(prevProviders => {
                                const newProviders = [...prevProviders];
                                newProviders[index] = { ...newProviders[index], photoUrl };
                                return newProviders;
                            });
                            console.log(`[Advertisement Popup] Foto cargada para: ${provider.name}`);
                        } catch (photoError) {
                            console.warn(`[Advertisement Popup] Error cargando foto para ${provider.name}:`, photoError);
                        }
                    }
                });

            } catch (error) {
                console.error('[Advertisement Popup] Error loading advertisement popup config:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchPopupData();
    }, []);

    const handleClose = () => {
        console.log('[Advertisement Popup] Cerrando popup...');
        setIsVisible(false);
        // Marcar que el usuario ya vio el popup en esta sesión
        sessionStorage.setItem('hasSeenAdvertisementPopup', 'true');
        console.log('[Advertisement Popup] sessionStorage actualizado:', sessionStorage.getItem('hasSeenAdvertisementPopup'));
    };

    const handleProviderClick = (username) => {
        console.log('[Advertisement Popup] Click en proveedor:', username);
        handleClose();
        navigate(`/${username}`);
    };

    const handleBackdropClick = (e) => {
        // Cerrar solo si se hace clic en el backdrop, no en el contenido
        if (e.target.classList.contains('adv-popup-backdrop')) {
            handleClose();
        }
    };

    // No mostrar nada si no es visible o está cargando
    if (!isVisible || loading || !popupData) {
        return null;
    }

    return (
        <div className="adv-popup-backdrop" onClick={handleBackdropClick}>
            <div className="adv-popup-container">
                <button className="adv-popup-close-btn" onClick={handleClose} aria-label="Cerrar">
                    <X size={24} />
                </button>

                <div className="adv-popup-header">
                    <h2 className="adv-popup-title">{popupData.title}</h2>
                    <p className="adv-popup-subtitle">{popupData.subtitle}</p>
                </div>

                <div className="adv-popup-content-grid">
                    {providers.map((provider) => (
                        <div
                            key={provider.username}
                            className="adv-popup-provider-card"
                            onClick={() => handleProviderClick(provider.username)}
                        >
                            <span className="adv-popup-provider-star">⭐</span>
                            <div className="adv-popup-provider-header">
                                <div className="adv-popup-provider-img-wrapper">
                                    <img
                                        src={provider.photoUrl || "/images/profile-preview-mobile.png"}
                                        alt={provider.name}
                                        onError={(e) => {
                                            e.target.src = "/images/profile-preview-mobile.png";
                                        }}
                                    />
                                </div>
                                <h3 className="adv-popup-provider-name">{provider.name}</h3>
                            </div>

                            <div className="adv-popup-provider-body">
                                <p className="adv-popup-provider-text">
                                    {provider.customText}
                                </p>

                                <div className="adv-popup-provider-locations">
                                    {provider.locations
                                        .sort((a, b) => a.localeCompare(b, 'es', { sensitivity: 'base' }))
                                        .slice(0, maxLocations)
                                        .map((location, index) => (
                                            <span key={index} className="adv-popup-location-chip">
                                                {location}
                                            </span>
                                        ))}
                                    {provider.locations.length > maxLocations && (
                                        <span className="adv-popup-location-chip adv-popup-location-more-chip">
                                            +{provider.locations.length - maxLocations}
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default AdvertisementPopup;

